import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import AddIcon from '@mui/icons-material/Add';
import DialogCadastrarCompensacao from "./DialogCadastrarCompensacao";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { formatarValorMonetario } from "../../../../../../../../../helpers/masks";
import moment from "moment";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { IInputRegCompensacao, IResultRegCompensacao } from "../../../../../../../../../types/mutations/financeiro/compensar-titulos-pagar-receber";
import BtnActionWrapper from "../../../../../../BtnActionWrapper";
import AlertMarcarDescontoCompensacao from "./AlertMarcarDescontoCompensacao";
import { IDataListaTitulosPagar } from "../../../../../../../../../types/api/financeiro/pesquisar-titulos-pagar";
import { PesquisarTitulosAReceber } from "../../../Step01";

export default function Compensasao() {

    const queryClient = useQueryClient();

    const { getValues, setValue, handleSubmit } = useFormContext();

    const [selected, setSelected] = useState<IDataListaTitulosPagar[]>([]);
    const [openModal, setOpenModal] = useState<IOpenModal>({
        state: false,
        preValue: null
    });
    const [rateioProporcional, setRateioProporcional] = useState(false);
    const [liquidarTerceiros, setLiquidarTerceiros] = useState(false);

    const { mutate: finalizarCompensacao, isLoading } = useMutation({
        mutationKey: 'financeiro/compensar-titulos-pagar-receber',
        mutationFn: (input: IInputRegCompensacao) => axiosClient.post(`financeiro/compensar-titulos-pagar-receber`, input),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar(`Erro ao registrar compensação`, { variant: "error" })
        },
        onSuccess: ({ data }: { data: IResultRegCompensacao }) => {
            queryClient.invalidateQueries("table-data-grid-financeiro/contas-receber/pesquisar-titulos-receber")
            enqueueSnackbar(`Compensação foi realizada com sucesso!`, { variant: "success" })

            const { recibo, recebido_por, data_hora, valor_recebido } = data
            const resultado = {
                recebido_por,
                data_hora,
                recibos: [{
                    recibo,
                    forma_recebimento: "Compensação",
                    valor: valor_recebido
                }]
            }
            setValue('recebimentoRegistrado', resultado)
        }
    });

    const handleOpenModal = (value: number | null = null) => {
        setOpenModal({
            state: true,
            preValue: value
        })
    }

    const handleCloseModal = () => {
        setOpenModal({
            state: false,
            preValue: null
        })
    }

    const handleBack = getValues('funcoesAux.onBack')

    const handleChangeSelected = (value: IDataListaTitulosPagar[]) => {
        setSelected(value)
    }

    const handleRemoveSelected = (id: string) => {
        const newItems = selected.filter((item: any) => item.id !== id);
        setSelected(newItems)
    }

    const handleSendCompensacao = () => {
        const { selecionar_titulos, finalizacao } = getValues()

        if (selected.length === 0) {
            enqueueSnackbar(`Selecione pelo menos um título para compensar!`, { variant: "warning" })
            return;
        }

        const filtroSaldo = selected.find((item) => item.saldo_principal <= 0)

        if (filtroSaldo) {
            enqueueSnackbar(`O título possui saldo negativo ou zero. Não é possível compensar`, { variant: "warning" })
            return;
        }

        const filtroData = selected.find((item) => {
            return moment(item.data_emissao).startOf('day').isAfter(moment(finalizacao.data_recebimento).startOf('day'))
        })

        if (filtroData) {
            enqueueSnackbar(`O título foi emitido após a data de recebimento.`, { variant: "warning" })
            return;
        }

        const primeiroTitulo = selecionar_titulos[0]
        const params: IInputRegCompensacao = {
            tipo_publico: primeiroTitulo.tipo_publico,
            codigo_publico: primeiroTitulo.codigo_publico,
            titulos_receber: selecionar_titulos.map((t: PesquisarTitulosAReceber) => t.id),
            titulos_pagar: selected.map((t: IDataListaTitulosPagar) => t.id),
            data: finalizacao.data_recebimento.format("YYYY-MM-DD"),
            descontar_diferenca: finalizacao.aplicarDiferencaComoDesconto,
            rateio_proporcional_receber_pagar: rateioProporcional
        }

        finalizarCompensacao(params)
    }

    const valorTotal = useMemo(() => {
        return selected.reduce((total, item: any) => {
            return total + (item.saldo_principal || 0);
        }, 0);
    }, [selected])

    return (
        <>
            <Box pb={2}>
                <Typography variant='h6' fontWeight="bold">Compensação</Typography>
                <Typography variant='subtitle2'>(Registra recebimento através de compensação de títulos a pagar)</Typography>
            </Box>

            <AlertMarcarDescontoCompensacao
                total_pagar={valorTotal}
            />

            <Stack direction="column" mb={1}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={rateioProporcional}
                            onChange={(_, checked) => setRateioProporcional(checked)}
                            color="primary"
                        />
                    }
                    label="Liquidar títulos proporcionalmente"
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={liquidarTerceiros}
                            onChange={(_, checked) => setLiquidarTerceiros(checked)}
                            color="primary"
                        />
                    }
                    label="Liquidar entre terceiros"
                />
            </Stack>

            {
                (selected.length <= 0) && (
                    <Box
                        display="flex"
                        alignItems="center"
                        flexDirection="column"
                        justifyContent="center"
                        borderRadius={1}
                        height={200}
                        border="1px solid #1B5E20"
                        gap={0.5}
                    >
                        <Typography variant="subtitle2">Clique no botão abaixo para adicionar os títulos a pagar</Typography>
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
                            Adicionar
                        </Button>
                    </Box>
                )
            }

            {
                (selected.length > 0) && (
                    <>
                        <Box mb={1} display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6">Títulos a compensar</Typography>

                            <Button size="small" variant="contained" startIcon={<EditIcon />} onClick={() => handleOpenModal()}>
                                Editar
                            </Button>
                        </Box>

                        <TableContainer sx={{ border: "1px solid #ccc", borderRadius: 2 }}>
                            <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Título</b></TableCell>
                                        <TableCell><b>Vencimento</b></TableCell>
                                        <TableCell align="right"><b>Valor</b></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {
                                        selected.map((item: any, index: number) => {
                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {item.id}
                                                    </TableCell>

                                                    <TableCell>
                                                        {moment(item.data_vencimento).format("DD/MM/YYYY")}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        {formatarValorMonetario(item.saldo_principal)}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                                                            <IconButton aria-label="delete" size="small" color="error" onClick={() => handleRemoveSelected(item.id)}>
                                                                <DeleteIcon fontSize="inherit" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })
                                    }

                                    <TableRow
                                        sx={{
                                            bgcolor: "#E8E8E8",
                                            '&:last-child td, &:last-child th': { border: 0 }
                                        }}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                        >
                                            <b>Valor total</b>
                                        </TableCell>

                                        <TableCell
                                            colSpan={3}
                                            align="right"
                                        >
                                            <b>{formatarValorMonetario(valorTotal)}</b>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )
            }

            <BtnActionWrapper>
                <Button onClick={handleBack} sx={{ pointerEvents: 'auto' }} >
                    Voltar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit(handleSendCompensacao)}
                    sx={{ pointerEvents: 'auto' }}
                    disabled={isLoading}
                >
                    {isLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                    Registrar
                </Button>
            </BtnActionWrapper>

            {
                openModal.state &&
                <DialogCadastrarCompensacao
                    {...{ selected, handleChangeSelected, liquidarTerceiros }}
                    open={openModal.state}
                    handleClose={() => handleCloseModal()}
                />
            }
        </>
    )
}

interface IOpenModal {
    state: boolean,
    preValue: number | null
}